import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/services/api';
import userManager from '@/services/userManager';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, Ear, Music, Calendar, Trash2, HelpCircle } from 'lucide-react';
// ProfessionalAudiogramImport removed for GDPR/HDS compliance
import PageHeader from '@/components/PageHeader';

const History = ({ t }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'tinnitus' ? 'tinnitus' : 'audiogram';
  const [audiometricTests, setAudiometricTests] = useState([]);
  const [tinnitusTests, setTinnitusTests] = useState([]);
  const [therapySessions, setTherapySessions] = useState([]);
  const [therapyStats, setTherapyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null); // { type: 'audiometric' | 'tinnitus', ids: [...] }
  const [showTherapyExplanation, setShowTherapyExplanation] = useState(null); // test_id to show explanation

  const isFr = !t?.locale || t?.locale !== 'en-US';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const userId = await userManager.getUserId();
      
      const [audio, tinnitus, therapy, stats] = await Promise.all([
        api.getAudiometricTests(userId),
        api.getTinnitusTests(userId),
        api.getTherapySessions(userId),
        api.getTherapyStats(userId)
      ]);

      console.log('[HISTORY][AUDIOMETRIC TESTS]', audio);

      const testsAround1804 = audio.filter(test =>
        test.date?.startsWith('2026-04-17') ||
        test.date?.startsWith('2026-04-18') ||
        test.date?.startsWith('2026-04-19')
      );
      console.log('[HISTORY][AROUND 18-04]', testsAround1804);

      setAudiometricTests(audio);
      setTinnitusTests(tinnitus);
      setTherapySessions(therapy);
      setTherapyStats(stats);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAudiometricTest = async (leftTestId, rightTestId) => {
    // Store for confirmation
    setConfirmDelete({ 
      type: 'audiometric', 
      ids: [leftTestId, rightTestId].filter(Boolean),
      message: t?.deleteTestConfirmAudiometric || 'Êtes-vous sûr de vouloir supprimer ce test audiométrique ?' 
    });
  };

  const handleDeleteTinnitusTest = async (testId) => {
    // Store for confirmation
    setConfirmDelete({ 
      type: 'tinnitus', 
      ids: [testId],
      message: t?.deleteTestConfirmTinnitus || 'Êtes-vous sûr de vouloir supprimer ce test acouphène ?' 
    });
  };

  const executeDelete = async () => {
    if (!confirmDelete) return;
    
    try {
      if (confirmDelete.type === 'audiometric') {
        // Delete all audiometric test IDs
        const deletePromises = confirmDelete.ids.map(id => api.deleteAudiometricTest(id));
        await Promise.all(deletePromises);
      } else if (confirmDelete.type === 'tinnitus') {
        // Delete tinnitus test
        await api.deleteTinnitusTest(confirmDelete.ids[0]);
      }
      
      // Reload data
      await loadData();
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting test:', error);
      alert(t?.errorDeletingTest || 'Erreur lors de la suppression du test');
      setConfirmDelete(null);
    }
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const prepareAudiogramData = () => {
    if (audiometricTests.length === 0) return [];
    
    // Get latest test for each ear
    const leftTest = audiometricTests.find(t => t.ear === 'left');
    const rightTest = audiometricTests.find(t => t.ear === 'right');

    const frequencies = [125, 250, 500, 1000, 2000, 4000, 8000];
    
    return frequencies.map(freq => {
      const dataPoint = { frequency: freq };
      
      if (leftTest) {
        const leftResult = leftTest.results.find(r => r.frequency === freq);
        if (leftResult) dataPoint.left = leftResult.threshold;
      }
      
      if (rightTest) {
        const rightResult = rightTest.results.find(r => r.frequency === freq);
        if (rightResult) dataPoint.right = rightResult.threshold;
      }
      
      return dataPoint;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] pt-20 px-8 pb-40">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12 text-white/60">
            {t.loading}
          </div>
        </div>
      </div>
    );
  }

  const audiogramData = prepareAudiogramData();

  return (
    <div className="min-h-screen bg-[#0A0F1C] pt-20 px-8 pb-40 relative overflow-hidden">
      {/* Halo premium top-left */}
      <div 
        className="absolute -top-32 -left-20 w-[320px] h-[320px] bg-[#6F42EA] opacity-[0.12] blur-[130px] pointer-events-none"
      />
      
      {/* Halo premium bottom-right */}
      <div 
        className="absolute bottom-0 right-[-40px] w-[280px] h-[280px] bg-[#3B48EE] opacity-[0.10] blur-[140px] pointer-events-none"
      />

      {/* Grain texture ultra léger */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          mixBlendMode: 'overlay'
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <PageHeader t={t} />
        
        {/* Card premium avec halo derrière le titre */}
        <div className="mb-6 relative">
          {/* Halo derrière le titre */}
          <div 
            className="absolute left-1/2 -translate-x-1/2 top-0 w-[180px] h-[180px] bg-white/10 blur-[90px] opacity-[0.15] rounded-full pointer-events-none"
          />
          
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 shadow-[0_10px_40px_rgba(0,0,0,0.55)] relative overflow-hidden">
            {/* Overlay dégradé léger */}
            <div 
              className="absolute inset-0 bg-gradient-to-r from-[#3B48EE]/12 via-transparent to-[#6F42EA]/8 pointer-events-none"
            />
            
            <div className="relative">
              <h1 className="text-white font-semibold tracking-wide text-2xl mb-1 drop-shadow-[0_6px_16px_rgba(255,255,255,0.10)]">
                {t.historyTitle}
              </h1>
              <p className="text-white/70 text-sm tracking-wide">
                {t.historyDesc}
              </p>
            </div>
          </div>
        </div>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 gap-2 bg-white/8 backdrop-blur-sm border border-white/10 rounded-2xl p-2 h-auto">
        <TabsTrigger
          value="audiogram"
          data-testid="tab-audiogram"
         className="min-w-0 h-auto rounded-xl px-3 py-3 flex items-center justify-center gap-2 text-center text-xs sm:text-sm leading-tight text-white/70 data-[state=active]:bg-white data-[state=active]:text-slate-900"
        >
          <Activity className="shrink-0" size={15} />
          <span className="truncate">{t?.historyTabAudiogram || 'Audiogrammes'}</span>
        </TabsTrigger>

        <TabsTrigger
         value="tinnitus"
         data-testid="tab-tinnitus"
         className="min-w-0 h-auto rounded-xl px-3 py-3 flex items-center justify-center gap-2 text-center text-xs sm:text-sm leading-tight text-white/70 data-[state=active]:bg-white data-[state=active]:text-slate-900"
        >
          <Ear className="shrink-0" size={15} />
          <span className="truncate">{t?.historyTabTinnitus || 'Acouphènes'}</span>
        </TabsTrigger>

        <TabsTrigger
          value="therapy"
          data-testid="tab-therapy"
          className="min-w-0 h-auto rounded-xl px-3 py-3 flex items-center justify-center gap-2 text-center text-xs sm:text-sm leading-tight text-white/70 data-[state=active]:bg-white data-[state=active]:text-slate-900"
        >
          <Music className="shrink-0" size={15} />
          <span className="truncate">{t?.historyTabTherapy || 'Séances'}</span>
        </TabsTrigger>
          </TabsList>

          {/* Audiogram Tab */}
          <TabsContent value="audiogram" className="space-y-4">
            {/* Professional Audiogram Import - REMOVED for GDPR/HDS compliance */}
            
            {/* Card premium avec style glass */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
              
              <div className="relative">
               <h2 className="text-white font-semibold text-lg mb-2 tracking-wide">
                 {isFr ? 'Votre audiogramme actuel' : 'Your current audiogram'}
               </h2>
               <p className="text-white/70 text-sm mb-1 tracking-wide">
                 {isFr ? 'Basé sur votre dernier test auditif' : 'Based on your latest hearing test'}
               </p>
               <p className="text-white/50 text-xs mb-6 tracking-wide">
                 {(() => {
                   const latestLeft = audiometricTests.find(t => t.ear === 'left');
                   const latestRight = audiometricTests.find(t => t.ear === 'right');
                   const latestDate = latestRight?.date || latestLeft?.date;
                   return latestDate
                     ? (isFr ? `Mesuré le ${formatDate(latestDate)}` : `Measured on ${formatDate(latestDate)}`)
                     : '';
                 })()}
               </p>
  
  {audiogramData.length > 0 ? (
                  <div className="h-96 bg-white/5 rounded-2xl p-4 border border-white/5">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={audiogramData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                          dataKey="frequency" 
                          label={{ value: `${t.frequency} (Hz)`, position: 'insideBottom', offset: -5, fill: '#fff', opacity: 0.7 }}
                          stroke="rgba(255,255,255,0.5)"
                          tick={{ fill: 'rgba(255,255,255,0.7)' }}
                        />
                        <YAxis 
                          reversed
                          label={{ value: `${t.threshold} (dB HL)`, angle: -90, position: 'insideLeft', fill: '#fff', opacity: 0.7 }}
                          domain={[0, 120]}
                          stroke="rgba(255,255,255,0.5)"
                          tick={{ fill: 'rgba(255,255,255,0.7)' }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: 'rgba(10, 15, 28, 0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', color: '#fff' }}
                          labelStyle={{ color: 'rgba(255,255,255,0.8)' }}
                        />
                        <Legend 
                          verticalAlign="top" 
                          align="center"
                          wrapperStyle={{ paddingBottom: '10px', color: '#fff' }}
                          iconType="line"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="left" 
                          stroke="#6F42EA" 
                          name={t.leftEar}
                          strokeWidth={3}
                          dot={{ fill: '#6F42EA', r: 5 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="right" 
                          stroke="#3B48EE" 
                          name={t.rightEar}
                          strokeWidth={3}
                          dot={{ fill: '#3B48EE', r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-12 text-white/50">
                    {t.noData}
                  </div>
                )}
              </div>
            </div>

            {/* Test History - Group by date */}
            {audiometricTests.length > 0 && (
              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
                
                <div className="relative">
                  <h2 className="text-white font-semibold text-lg mb-4 tracking-wide">{t.testHistory}</h2>
                  
                  <div className="space-y-3">
                    {(() => {
                      // Group tests by date (assuming tests for both ears are done at similar times)
                      const testsByDate = {};
                      audiometricTests.forEach(test => {
                        const dateKey = test.date.split('T')[0]; // Group by date only (ignore time)
                        if (!testsByDate[dateKey]) {
                          testsByDate[dateKey] = {
                            date: test.date,
                            left: null,
                            right: null
                          };
                        }
                        if (test.ear === 'left') testsByDate[dateKey].left = test;
                        if (test.ear === 'right') testsByDate[dateKey].right = test;
                      });

                      // Convert to array and sort by date (newest first)
                      const groupedTests = Object.values(testsByDate).sort((a, b) => 
                        new Date(b.date) - new Date(a.date)
                      );

                      console.log('[HISTORY][GROUPED TESTS]', groupedTests);

                      const groupedTestToday = groupedTests.find(group =>
                        group.date?.startsWith('2026-04-20')
                      );
                      console.log('[HISTORY][GROUPED TEST TODAY]', groupedTestToday);

                      return groupedTests.map((testGroup, index) => (
                        <div 
                          key={index} 
                          className="relative overflow-hidden rounded-xl bg-white/5 backdrop-blur-md border border-white/10 p-3 flex justify-between items-center group hover:scale-[1.01] transition-all duration-200"
                          style={{
                            animation: `fadeSlideUp 200ms ease-out ${index * 30}ms both`
                          }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-[#3B48EE]/8 via-transparent to-transparent pointer-events-none" />
                          
                          <div className="flex items-center gap-3 flex-1 relative">
                            <div className="bg-white/10 backdrop-blur-md rounded-lg p-2 border border-white/15">
                              <Calendar size={16} className="text-white/80" />
                            </div>
                            <div>
                              <span className="text-sm text-white font-medium block">{formatDate(testGroup.date)}</span>
                              <span className="text-xs text-white/60">
                                {(() => {
                                const leftCount = testGroup.left?.results?.length || 0;
                                const rightCount = testGroup.right?.results?.length || 0;
                                const maxCount = Math.max(leftCount, rightCount);

                                return maxCount > 0
                                  ? `${maxCount} ${t.frequencies}`
                                  : `${t.incomplete || "Incomplet"}`;
                              })()}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAudiometricTest(
                                testGroup.left?.id || null,
                                testGroup.right?.id || null
                              );
                            }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity relative"
                            title="Supprimer ce test"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Tinnitus Tab */}
          <TabsContent value="tinnitus" className="space-y-4">
            {/* Evolution Charts - Separated for clarity */}
            {tinnitusTests.length >= 1 && (() => {
              // Combine frequency and volume data by date, sorted chronologically
              // Transform and sort by date, ensuring unique timestamps for each point
              const evolutionData = tinnitusTests.map((test, origIndex) => {
                const dateObj = new Date(test.date);
                const dateStr = dateObj.toLocaleDateString();
                const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                
                // Ensure volume is properly extracted (handle both number and null/undefined)
                const volume = test.volume_db_spl != null ? Number(test.volume_db_spl) : null;
                
                return {
                  id: `test-${origIndex}`, // Unique ID
                  date: dateStr,
                  dateTime: `${dateStr} ${timeStr}`, // Full datetime for display
                  dateSort: dateObj.getTime(),
                  timestamp: dateObj.getTime() + origIndex, // Add tiny offset to guarantee uniqueness in Recharts
                  frequency: test.detected_frequency,
                  volume: volume,
                  rawDate: test.date // Keep original for debugging
                };
              }).sort((a, b) => a.dateSort - b.dateSort);
              
              // Debug logging
              console.log('📊 Evolution data:', evolutionData);
              
              const hasVolumeData = evolutionData.some(d => d.volume !== null && d.volume !== undefined && d.volume > 0);
              
              return (
                <>
                  {/* Volume Chart - PRIMARY metric */}
                  {hasVolumeData && (
                    <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-6 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
                      
                      <div className="relative">
                        <div className="flex items-center gap-2 mb-2">
                          <Ear className="w-5 h-5 text-[#6F42EA]" />
                          <h2 className="text-white font-semibold text-lg tracking-wide">
                            {t?.tinnitusVolumeEvolution || 'Évolution du Volume des Acouphènes'}
                          </h2>
                        </div>
                        <p className="text-white/70 text-sm mb-6 tracking-wide">
                          {t.tinnitusVolumeDesc || 'Volume perçu en dB SPL - Objectif : diminution au fil du temps'}
                        </p>
                        
                        <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                          <ResponsiveContainer width="100%" height={280}>
                          <LineChart data={evolutionData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                            <XAxis 
                              dataKey="timestamp"
                              type="number"
                              domain={['dataMin', 'dataMax']}
                              tickFormatter={(timestamp) => {
                                const date = new Date(timestamp);
                                const dateStr = date.toLocaleDateString();
                                const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                return `${dateStr} ${timeStr}`;
                              }}
                              label={{ value: t.date || 'Date', position: 'insideBottom', offset: -10, fill: '#fff', opacity: 0.7 }}
                              tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.7)' }}
                              angle={-25}
                              textAnchor="end"
                              height={70}
                              stroke="rgba(255,255,255,0.5)"
                            />
                            <YAxis 
                              label={{ value: 'Volume (dB SPL)', angle: -90, position: 'insideLeft', fill: '#fff', opacity: 0.7 }}
                              domain={[0, 'dataMax + 3']}
                              stroke="rgba(255,255,255,0.5)"
                              tick={{ fill: 'rgba(255,255,255,0.7)' }}
                            />
                            <Tooltip 
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-[#0A0F1C]/95 backdrop-blur-lg p-3 border border-white/20 rounded-xl shadow-lg">
                                      <p className="font-semibold text-sm text-white mb-2">{data.dateTime}</p>
                                      {data.volume !== null && (
                                        <p className="text-sm text-[#6F42EA] font-medium">
                                          Volume: {data.volume.toFixed(1)} dB SPL
                                        </p>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Legend 
                              verticalAlign="top" 
                              align="center"
                              wrapperStyle={{ paddingBottom: '10px', color: '#fff' }}
                              iconType="line"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="volume" 
                              stroke="#6F42EA" 
                              strokeWidth={4}
                              name={t.perceivedVolume || "Volume perçu (dB SPL)"}
                              dot={{ r: 7, fill: '#6F42EA', stroke: '#fff', strokeWidth: 2 }}
                              connectNulls={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Frequency Chart - SECONDARY metric */}
                  <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
                    
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <Ear className="w-5 h-5 text-[#3B48EE]" />
                        <h2 className="text-white font-semibold text-lg tracking-wide">
                          {t?.tinnitusFrequencyEvolution || 'Évolution de la Fréquence des Acouphènes'}
                        </h2>
                      </div>
                      <p className="text-white/70 text-sm mb-6 tracking-wide">
                        {t.tinnitusFrequencyDesc || 'Fréquence détectée en Hz au fil du temps'}
                      </p>
                      
                      <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                        <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={evolutionData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis 
                            dataKey="timestamp"
                            type="number"
                            domain={['dataMin', 'dataMax']}
                            tickFormatter={(timestamp) => {
                              const date = new Date(timestamp);
                              const dateStr = date.toLocaleDateString();
                              const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              return `${dateStr} ${timeStr}`;
                            }}
                            label={{ value: t.date || 'Date', position: 'insideBottom', offset: -10, fill: '#fff', opacity: 0.7 }}
                            tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.7)' }}
                            angle={-25}
                            textAnchor="end"
                            height={70}
                            stroke="rgba(255,255,255,0.5)"
                          />
                          <YAxis 
                            label={{ value: t.frequencyHz || 'Fréquence (Hz)', angle: -90, position: 'insideLeft', fill: '#fff', opacity: 0.7 }}
                            domain={['dataMin - 500', 'dataMax + 500']}
                            stroke="rgba(255,255,255,0.5)"
                            tick={{ fill: 'rgba(255,255,255,0.7)' }}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-[#0A0F1C]/95 backdrop-blur-lg p-3 border border-white/20 rounded-xl shadow-lg">
                                    <p className="font-semibold text-sm text-white mb-2">{data.dateTime}</p>
                                    <p className="text-sm text-[#3B48EE] font-medium">
                                      {t?.frequency || 'Fréquence'}: {data.frequency.toFixed(0)} Hz
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend 
                            verticalAlign="top" 
                            align="center"
                            wrapperStyle={{ paddingBottom: '10px', color: '#fff' }}
                            iconType="line"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="frequency" 
                            stroke="#3B48EE" 
                            strokeWidth={3}
                            name={t.frequencyHz || "Fréquence (Hz)"}
                            dot={{ r: 6, fill: '#3B48EE', stroke: '#fff', strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
            
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
              
              <div className="relative">
                <h2 className="text-white font-semibold text-lg mb-2 tracking-wide">{t.tinnitusHistory}</h2>
                <p className="text-white/70 text-sm mb-6 tracking-wide">{t.tinnitusHistoryDesc}</p>
                
                {tinnitusTests.length > 0 ? (
                  <div className="space-y-4">
                    {tinnitusTests.map((test, index) => {
                      // Determine color based on frequency range (premium colors)
                      const getFrequencyColor = (freq) => {
                        if (freq < 1000) return { bg: 'bg-blue-500/20', bar: 'bg-blue-500', text: 'text-blue-300', label: t.lowFrequency || 'Grave' };
                        if (freq < 4000) return { bg: 'bg-green-500/20', bar: 'bg-green-500', text: 'text-green-300', label: t.midFrequency || 'Moyen' };
                        if (freq < 8000) return { bg: 'bg-orange-500/20', bar: 'bg-orange-500', text: 'text-orange-300', label: t.highFrequency || 'Aigu' };
                        return { bg: 'bg-red-500/20', bar: 'bg-red-500', text: 'text-red-300', label: t.veryHighFrequency || 'Très aigu' };
                      };
                      
                      const colorScheme = getFrequencyColor(test.detected_frequency);
                      const barWidth = Math.min((test.detected_frequency / 20000) * 100, 100); // Max 20kHz
                      
                      return (
                        <div 
                          key={test.id}
                          className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-lg border border-white/10 shadow-[0_6px_22px_rgba(0,0,0,0.45)] p-4 group hover:scale-[1.01] transition-all duration-200"
                          style={{
                            animation: `fadeSlideUp 200ms ease-out ${index * 40}ms both`
                          }}
                        >
                          <div className={`absolute inset-0 ${colorScheme.bg} opacity-30 pointer-events-none`} />
                          
                          <div className="flex justify-between items-start mb-3 relative">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="bg-white/10 backdrop-blur-md rounded-lg p-2 border border-white/15">
                                  <Calendar size={16} className="text-white/80" />
                                </div>
                                <span className="text-sm font-medium text-white">{formatDate(test.date)}</span>
                              </div>
                              
                              {/* Mini Spectrogram - Modern Design - Zoomed on detected frequency */}
                              <div className="mb-3 bg-gradient-to-b from-gray-900 to-black rounded-xl p-3 shadow-inner">
                                <div className="flex items-end justify-center h-20 gap-[2px]">
                                  {Array.from({ length: 50 }).map((_, i) => {
                                    // Zoom window: ±100 Hz around detected frequency
                                    const zoomRange = 100; // Hz on each side
                                    const minFreq = test.detected_frequency - zoomRange;
                                    const maxFreq = test.detected_frequency + zoomRange;
                                    const barFreq = minFreq + (i / 50) * (maxFreq - minFreq);
                                    
                                    // Calculate height based on proximity to detected frequency
                                    const freqDiff = Math.abs(barFreq - test.detected_frequency);
                                    const proximity = Math.max(0, 1 - (freqDiff / zoomRange));
                                    const height = 20 + proximity * 80; // 20% to 100% height
                                    
                                    // Determine bar color based on DETECTED frequency
                                    let barColor = 'bg-gray-600'; // Default gray
                                    let glowColor = 'shadow-gray-500/50';
                                    
                                    if (proximity > 0.5) { // Only color bars very close to detected frequency
                                      if (test.detected_frequency >= 8000) {
                                        barColor = 'bg-red-500';
                                        glowColor = 'shadow-red-500/50';
                                      } else if (test.detected_frequency >= 4000) {
                                        barColor = 'bg-orange-500';
                                        glowColor = 'shadow-orange-500/50';
                                      } else if (test.detected_frequency >= 1000) {
                                        barColor = 'bg-green-500';
                                        glowColor = 'shadow-green-500/50';
                                      } else {
                                        barColor = 'bg-blue-500';
                                        glowColor = 'shadow-blue-500/50';
                                      }
                                    }
                                    
                                    const opacity = proximity > 0.5 ? (0.6 + proximity * 0.4) : 0.3;
                                    
                                    return (
                                      <div
                                        key={i}
                                        className={`flex-1 ${barColor} rounded-t-sm transition-all duration-500 ${proximity > 0.9 ? `shadow-lg ${glowColor}` : ''}`}
                                        style={{ 
                                          height: `${height}%`,
                                          opacity: opacity
                                        }}
                                      />
                                    );
                                  })}
                                </div>
                                {/* Frequency scale with zoomed range */}
                                <div className="relative mt-2 h-4">
                                  <div className="absolute inset-0 flex justify-between items-center text-[9px] text-gray-400">
                                    <span>{Math.round(test.detected_frequency - 100)} Hz</span>
                                    <span className="text-white font-bold">{test.detected_frequency} Hz</span>
                                    <span>{Math.round(test.detected_frequency + 100)} Hz</span>
                                  </div>
                                </div>
                                <div className="text-center text-[10px] text-gray-500 mt-1">Zoom ±100 Hz</div>
                              </div>
                              
                              {/* Frequency Bar */}
                              <div className="mb-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-2xl font-bold text-white drop-shadow-[0_4px_12px_rgba(255,255,255,0.15)]">
                                    {test.detected_frequency} Hz
                                  </span>
                                  <span className={`text-xs px-2 py-1 rounded-full ${colorScheme.bar} text-white shadow-md`}>
                                    {colorScheme.label}
                                  </span>
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden backdrop-blur-sm">
                                  <div 
                                    className={`${colorScheme.bar} h-full rounded-full transition-all duration-500 shadow-lg`}
                                    style={{ width: `${barWidth}%` }}
                                  ></div>
                                </div>
                                <div className="flex justify-between text-xs text-white/60 mt-1">
                                  <span>0 Hz</span>
                                  <span>20 kHz</span>
                                </div>
                              </div>
                              
                              {/* Tinnitus Type & Volume */}
                              <div className="flex gap-4 text-xs text-white/90 font-medium">
                                {test.tinnitus_type && (
                                  <div>
                                    <span className="font-bold text-white">Type:</span> {test.tinnitus_type}
                                  </div>
                                )}
                                {test.volume_db_spl && (
                                  <div>
                                    <span className="font-bold text-white">Volume:</span> {test.volume_db_spl} dB SPL
                                  </div>
                                )}
                                {(test.confidence_level || test.volume_db_spl) && (
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-white">{t.therapyAdjustment || 'Ajustement sonore'}:</span> 
                                      <span className="text-white/90">{
                                        // Adaptive gain calculation (matches Lab Mode Notch logic)
                                        // Attenuation proportional to perceived volume (with ceiling)
                                        (() => {
                                          let gain;
                                          if (test.volume_db_spl) {
                                            gain = -Math.min(test.volume_db_spl, 15); // Max -15 dB attenuation
                                          } else if (test.confidence_level) {
                                            gain = -Math.min(test.confidence_level, 12); // Max -12 dB
                                          } else {
                                            gain = -10; // Default
                                          }
                                          return gain > 0 ? `+${gain}` : gain;
                                        })()
                                      } dB</span>
                                      <button
                                        onClick={() => setShowTherapyExplanation(showTherapyExplanation === test._id ? null : test._id)}
                                        className="text-[#6F42EA] hover:text-[#8B3EEC] transition-colors"
                                        title={t.learnMore || 'En savoir plus'}
                                      >
                                        <HelpCircle className="h-4 w-4" />
                                      </button>
                                    </div>
                                    {showTherapyExplanation === test._id && (
                                      <div className="mt-2 p-4 border-2 border-[#6F42EA] rounded-xl text-sm shadow-lg space-y-3 bg-white/10 backdrop-blur-lg">
                                        <p className="font-bold text-base text-[#6F42EA]">{t.therapyMethodExplained || '🎯 Méthode sonore'}</p>
                                        <p className="font-semibold italic border-l-4 border-[#6F42EA] p-3 rounded bg-white/5 text-white/90">
                                          {t.therapyPhilosophy || "Il ne s'agit pas d'éteindre le son. Il s'agit d'apprendre à le faire disparaître naturellement."}
                                        </p>
                                        {(() => {
                                          // Calculate adaptive gain
                                          let gain;
                                          if (test.volume_db_spl) {
                                            gain = -Math.min(test.volume_db_spl, 15);
                                          } else if (test.confidence_level) {
                                            gain = -Math.min(test.confidence_level, 12);
                                          } else {
                                            gain = -10;
                                          }
                                          
                                          const isNotch = gain < 0;
                                          const isInverseNotch = gain > 0;
                                          
                                          return (
                                            <>
                                              <div className="p-3 rounded border border-white/20 bg-white/5">
                                                <p className="text-white/90">
                                                  <strong>{t.measuredVolume || 'Volume mesuré'}:</strong> {test.volume_db_spl} dB SPL
                                                </p>
                                                <p className="text-white/90">
                                                  <strong>
                                                    {isNotch 
                                                      ? (t.appliedAttenuation || 'Atténuation appliquée')
                                                      : (t.appliedBoost || 'Boost appliqué')
                                                    }:
                                                  </strong> {gain > 0 ? `+${gain}` : gain} dB
                                                </p>
                                              </div>
                                              <p className="border-t-2 border-white/20 pt-3 leading-relaxed text-white/80">
                                                {isNotch 
                                                  ? (t.therapyPrincipleNotch || 
                                                      "L'atténuation appliquée est proportionnelle au volume de votre acouphène (max -15 dB). Cette méthode Notch classique cible précisément la fréquence de l'acouphène pour réduire sa perception. Le filtre notch atténue le signal sonore à la fréquence problématique, permettant au cerveau de moins se concentrer sur l'acouphène. Cette approche favorise l'habituation naturelle et la réorganisation neuronale.")
                                                  : (t.therapyPrincipleInverseNotch || 
                                                      "Le boost appliqué cible la fréquence de votre acouphène pour stimuler la neuroplasticité. Cette méthode Inverse Notch amplifie légèrement le signal à la fréquence problématique, encourageant le cerveau à se recalibrer et à réduire sa sensibilité excessive. Cette approche de 'réentraînement neuronal' vise à réorganiser les circuits auditifs du cerveau pour diminuer progressivement la perception de l'acouphène.")
                                                }
                                              </p>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTinnitusTest(test.id);
                              }}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity relative"
                              title="Supprimer ce test"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-white/50">
                    {t.noData}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Therapy Tab */}
          <TabsContent value="therapy" className="space-y-4">
            {therapyStats && (
              <div className="grid md:grid-cols-3 gap-4">
                {/* Stats Card 1 */}
                <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-5 shadow-[0_6px_22px_rgba(0,0,0,0.45)] relative overflow-hidden group hover:scale-[1.02] transition-transform duration-200">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/15 via-transparent to-transparent pointer-events-none" />
                  <div className="relative">
                    <h3 className="text-white/80 font-medium text-sm mb-3 tracking-wide">{t.totalSessions}</h3>
                    <div className="text-4xl font-bold text-white drop-shadow-[0_4px_12px_rgba(255,255,255,0.15)]">
                      {therapyStats.total_sessions}
                    </div>
                  </div>
                </div>

                {/* Stats Card 2 */}
                <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-5 shadow-[0_6px_22px_rgba(0,0,0,0.45)] relative overflow-hidden group hover:scale-[1.02] transition-transform duration-200">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#6F42EA]/15 via-transparent to-transparent pointer-events-none" />
                  <div className="relative">
                    <h3 className="text-white/80 font-medium text-sm mb-3 tracking-wide">{t.totalTime}</h3>
                    <div className="text-4xl font-bold text-white drop-shadow-[0_4px_12px_rgba(255,255,255,0.15)]">
                      {therapyStats.total_duration_minutes}
                    </div>
                    <div className="text-sm text-white/60 mt-1">{t.minutes}</div>
                  </div>
                </div>

                {/* Stats Card 3 */}
                <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-5 shadow-[0_6px_22px_rgba(0,0,0,0.45)] relative overflow-hidden group hover:scale-[1.02] transition-transform duration-200">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#8B3EEC]/15 via-transparent to-transparent pointer-events-none" />
                  <div className="relative">
                    <h3 className="text-white/80 font-medium text-sm mb-3 tracking-wide">{t.favoriteTherapy}</h3>
                    <div className="text-lg font-semibold text-white drop-shadow-[0_4px_12px_rgba(255,255,255,0.15)]">
                      {therapyStats.therapy_types && Object.keys(therapyStats.therapy_types).length > 0
                        ? (
                            Object.entries(therapyStats.therapy_types).sort((a, b) => b[1] - a[1])[0][0] === 'therapeutic'
                              ? (t?.therapeuticSound || 'Paysage sonore')
                              : Object.entries(therapyStats.therapy_types).sort((a, b) => b[1] - a[1])[0][0]
                          )
                        : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sessions history container premium */}
            <div className="bg-white/5 backdrop-blur-[3px] rounded-3xl border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-4 py-5 relative overflow-hidden">
              {/* Overlay dégradé */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
              
              <div className="relative">
                <h2 className="text-white font-semibold text-lg mb-2 tracking-wide">{t.therapyHistory}</h2>
                <p className="text-white/70 text-sm mb-4 tracking-wide">{t.therapyHistoryDesc}</p>
                
              <div className="text-white/45 text-xs mb-4">
                {therapySessions.length > 20
                  ? `20 dernières séances sur ${therapySessions.length}`
                  : `${therapySessions.length} séance${therapySessions.length > 1 ? 's' : ''}`}
              </div>

                {therapySessions.length > 0 ? (
                  <div className="space-y-4">
                    {therapySessions.slice(0, 20).map((session, index) => (
                      <div 
                        key={session.id}
                        className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-lg border border-white/10 shadow-[0_6px_22px_rgba(0,0,0,0.45)] px-4 py-3 flex items-center justify-between gap-3 group hover:scale-[1.01] transition-all duration-200"
                        style={{
                          animation: `fadeSlideUp 200ms ease-out ${index * 30}ms both`
                        }}
                      >
                        {/* Overlay dégradé léger */}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#3B48EE]/12 via-transparent to-[#6F42EA]/8 pointer-events-none" />
                        
                        {/* Colonne gauche */}
                        <div className="flex items-center gap-3 relative">
                          {/* Icône calendrier dans carré glass */}
                          <div className="bg-white/10 backdrop-blur-md rounded-xl p-2 border border-white/15 shadow-md flex items-center justify-center">
                            <Calendar size={16} className="text-white/80" />
                          </div>
                          
                          <div>
                            <div className="text-white font-medium text-sm">
                              {formatDate(session.date)}
                            </div>
                            <div className="text-white/75 text-xs mt-0.5">
                              {(() => {
                                const typeMap = {
                                  therapeutic: t?.therapeuticSound || 'Paysage sonore',

                                  'classique': t.classique || 'Classique',
                                  'piano_ambiant': t.pianoAmbiant || 'Ambient',
                                  'chill_lofi': t.chillLofi || 'Chill / Lofi',
                                  'respiration': t.respiration || 'Respiration',
                                  'nature': t.nature || 'Nature',
                                  'sommeil': t.sommeil || 'Sleep',
                                  'pulse_flow': t.pulseFlow || 'Pulse Flow'
                                };
                                return typeMap[session.therapy_type] || session.therapy_type.replace('_', ' ');
                              })()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Colonne droite - durée */}
                        <div className="text-right relative">
                          <div className="text-white font-semibold text-base">
                            {Math.floor(session.duration / 60)}:{(session.duration % 60).toString().padStart(2, '0')}
                          </div>
                          <div className="text-white/60 text-xs">{t.minutes}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-white/50">
                    {t.noData}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirmation Modal - Premium */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200" onClick={cancelDelete}>
          <div 
            className="w-96 max-w-md bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-6 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'fadeSlideUp 250ms ease-out'
            }}
          >
            {/* Overlay dégradé */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#6F42EA]/15 via-transparent to-[#3B48EE]/10 pointer-events-none" />
            
            <div className="relative">
              <h2 className="text-white font-semibold text-xl mb-2 tracking-wide">Confirmer la suppression</h2>
              <p className="mb-6 text-white/70 text-sm">{confirmDelete.message}</p>
              <div className="flex justify-end gap-3">
                <Button 
                  variant="outline" 
                  onClick={cancelDelete}
                  className="bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-md"
                >
                  Annuler
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={executeDelete}
                  className="bg-red-500 hover:bg-red-600 text-white shadow-lg"
                >
                  Supprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;